import React, {useCallback, useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Table, WindowScroller} from "react-virtualized";
import stocksActionsFactory from "../stocksActions";
import ideaActionsFactory from "../../idea/ideaActions";
import StockRow from "./StockRow";
import {log} from "../../../logging/logEvent";
import {tableBorderColour} from "../../../style/styleConsts";
import SortArrow from "../../elements/SortArrow";
import {
    earningsWidth, latestActivityWidth, openIdeasWidth, orgNameWidth, priceChangeTodayChangeWidth,
    sentimentOneDayChangeWidth, sentimentTodayValueWidth, tickerWidth
} from "./stockStyles";
import {useDebounced} from "../../../hooks/Debounced";

const cellPadding = {padding: '6px 5px'};

export const stockTableWidth = 944;

const headerStyle = ({
    ...cellPadding,
    marginLeft: 0,
    flex: "0 1 auto",
    msFlex: "0 1 auto",
    fontWeight: "normal",
    textTransform: "none"
});

export const unselectedRowHeight = 31;
export const selectedRowHeight = (numChildren) => (unselectedRowHeight + 13) + numChildren * 37;

function headerRenderer(className, style, changeSortTo, currentSort) {
    function compositeHeaderRow(field, heading) {
        const headerElements = [<span key='title'>{heading}</span>];
        const columnClassName = "t-" + field.replace(/([A-Z])/g, function ($1) {
            return "-" + $1.toLowerCase();
        });
        return <div role="columnheader" className={"ReactVirtualized__Table__headerColumn " + columnClassName} style={{
            ...headerStyle,
            fontWeight: "bold",
            borderStyle: "solid",
            borderWidth: "0px 0px 1px 0px",
            borderColor: tableBorderColour,
            flexBasis: "auto"
        }}>{headerElements}</div>
    }

    function isAlphabetical(field) {
        return field === 'bloomberg' || field === 'name';
    }

    function headerRow(field, heading, widthStyle) {
        const headerElements = [<span key='title' style={{cursor: 'pointer'}}>{heading}</span>];
        if (field === currentSort.field) {
            let arrowUp = currentSort.reversed;
            if (isAlphabetical(field)) {
                arrowUp = !arrowUp;
            }
            const sortIcon = arrowUp ? <SortArrow dir="ASC"/> : <SortArrow dir="DESC"/>;
            headerElements.push(<span key='sort' style={{fontSize: 'smaller'}}>{sortIcon}</span>);
        }
        const columnClassName = "t-" + field.replace(/([A-Z])/g, function ($1) {
            return "-" + $1.toLowerCase();
        });
        return <div role="columnheader" className={"ReactVirtualized__Table__headerColumn " + columnClassName}
                    style={{...headerStyle, ...widthStyle}}
                    onClick={() => changeSortTo(field)}>{headerElements}</div>
    }

    return <div role="row" style={style}
                className={className + " t-header-row"}>
        <div style={{marginLeft: 10}}>
            <div>
                {compositeHeaderRow("stocksHeader", "Stock")}
            </div>
            <div style={{display: "flex"}}>
                {headerRow("bloomberg", "Symbol", tickerWidth)}
                {headerRow("name", "Name", orgNameWidth)}
            </div>
        </div>
        <div>
            <div>
                {compositeHeaderRow("contributorActivityHeader", "Your Contributors")}
            </div>
            <div style={{display: "flex"}}>
                {headerRow("openIdeas", "Open Ideas", openIdeasWidth)}
                {headerRow("latestActivity", "Latest", latestActivityWidth)}
            </div>
        </div>
        <div>
            <div>
                {compositeHeaderRow("timNetworkHeader", "Whole TIM Network")}
            </div>
            <div style={{display: "flex"}}>
                {headerRow("sentimentTodayValue", "Sentiment", sentimentTodayValueWidth)}
                {headerRow("sentimentOneDayChange", "1d Change", sentimentOneDayChangeWidth)}
            </div>
        </div>
        <div>
            <div>
                {compositeHeaderRow("marketActivityHeader", "Market Activity")}
            </div>
            <div style={{display: "flex"}}>
                {headerRow("priceChangeToday", "Price Today", priceChangeTodayChangeWidth)}
                {headerRow("earnings", "Earnings", earningsWidth)}
            </div>
        </div>
    </div>
}

const StocksTableContent = ({sortedList, selectedRowOrgId, currentSort, selectRow, changeSortTo, showIdeaDetail, attachRecomputeRowHeights}) => {
    const scrollChanged = useDebounced(({stopIndex: newStopIndex}) => {
        log("VisibleStocksChanged",
            {
                sortField: currentSort.field,
                sortReversed: currentSort.reversed,
                lastStockIndex: newStopIndex
            })
    }, 2000, []);

    const heightFunction = ({index}) => {
        return sortedList[index].orgId === selectedRowOrgId ? selectedRowHeight(sortedList[index].activity.length) : unselectedRowHeight;
    };

    return <WindowScroller>
        {({height, isScrolling, scrollTop}) => (
            <Table
                autoHeight
                height={height}
                width={stockTableWidth}
                gridStyle={{outline: "none"}}
                headerHeight={60}
                rowHeight={heightFunction}
                rowCount={sortedList.length}
                rowGetter={({index}) => sortedList[index]}
                onRowsRendered={scrollChanged}
                headerRowRenderer={(inputData) => headerRenderer(inputData.className, inputData.style, changeSortTo, currentSort)}
                rowRenderer={(rowInput) => <StockRow key={rowInput.rowData.bloomberg}
                                                     showIdeaDetail={showIdeaDetail}
                                                     stock={rowInput.rowData} style={rowInput.style}
                                                     rowSelectFunction={() => selectRow(rowInput.rowData.orgId)}
                                                     renderExpanded={selectedRowOrgId === rowInput.rowData.orgId}/>}
                scrollTop={scrollTop}
                ref={attachRecomputeRowHeights}
            />)}
    </WindowScroller>
};

const StocksTable = ({}) => {
    const {list: stocks, sort, selectedRow} = useSelector(({stocks: {list, sort, selectedRow}}) => ({list, sort, selectedRow}));
    const dispatch = useDispatch();
    const tableRef = useRef(null);
    const attachRecomputeRowHeights = useCallback(vtable => {
        tableRef.current = vtable;
    }, []);
    useEffect(() => {
        if (tableRef.current) {
            tableRef.current.recomputeRowHeights();
        }
    });

    const stocksActions = stocksActionsFactory(dispatch);
    const ideaActions = ideaActionsFactory(dispatch);

    return <StocksTableContent sortedList={stocks}
                               selectedRowOrgId={selectedRow}
                               currentSort={sort}
                               selectRow={stocksActions.selectRow}
                               changeSortTo={stocksActions.changeSort}
                               showIdeaDetail={ideaActions.showDetail}
                               attachRecomputeRowHeights={attachRecomputeRowHeights}/>;
};

export default StocksTable;
